import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, BarChart3, Megaphone, Inbox as InboxIcon,
  ServerCog, LogOut, Menu, X, Star, PenLine,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  {
    group: "Beheer",
    items: [
      { key: "dashboard", label: "Live Dashboard", to: "/admin", icon: LayoutDashboard, end: true },
      { key: "leads", label: "Leads", to: "/admin/leads", icon: Users },
      { key: "analytics", label: "Analytics", to: "/admin/analytics", icon: BarChart3 },
    ],
  },
  {
    group: "Marketing",
    items: [
      { key: "marketing", label: "Mailmarketing", to: "/admin/marketing", icon: Megaphone },
      { key: "reviews", label: "Beoordelingen", to: "/admin/reviews", icon: Star },
      { key: "inbox", label: "Inbox", to: "/admin/inbox", icon: InboxIcon },
      { key: "signature", label: "E-mailhandtekening", to: "/admin/signature", icon: PenLine },
    ],
  },
  {
    group: "Systeem",
    items: [
      { key: "system", label: "Systeemmeldingen", to: "/admin/system", icon: ServerCog },
    ],
  },
];

const useClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
};

export default function AdminLayout({ title, subtitle, actions, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const now = useClock();

  const doLogout = () => { logout(); navigate("/admin/login", { replace: true }); };

  const isActive = (item) =>
    item.end ? location.pathname === item.to
             : location.pathname === item.to || location.pathname.startsWith(item.to + "/");

  return (
    <div className="min-h-screen bg-cc-bg text-cc-ink font-body">
      <aside className={`fixed inset-y-0 left-0 z-40 w-[264px] bg-cc-panel border-r border-cc-border flex flex-col transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="h-16 flex items-center gap-3 px-5 border-b border-cc-border">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-cc-accent text-white font-heading font-extrabold shrink-0">V</span>
          <div className="min-w-0">
            <p className="font-heading font-extrabold text-[15px] leading-none text-cc-ink truncate">VHM <span className="text-cc-accent">Renovation</span></p>
            <p className="font-body text-[10px] uppercase tracking-[0.2em] text-cc-faint mt-1">Command Center</p>
          </div>
          <button onClick={() => setOpen(false)} className="ml-auto lg:hidden text-cc-faint"><X className="w-5 h-5" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-5 px-3 space-y-6">
          {NAV.map((grp) => (
            <div key={grp.group}>
              <p className="px-3 mb-2 font-body text-[10px] font-bold uppercase tracking-[0.18em] text-cc-faint">{grp.group}</p>
              <div className="space-y-1">
                {grp.items.map((item) => {
                  const active = isActive(item);
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.key}
                      to={item.to}
                      onClick={() => setOpen(false)}
                      data-testid={`nav-${item.key}`}
                      className={`flex items-center gap-3 rounded-xl px-3 py-2.5 font-body text-sm font-medium transition-colors ${active ? "bg-cc-accent text-white shadow-lg" : "text-cc-sub hover:bg-cc-panel2 hover:text-cc-ink"}`}
                    >
                      <Icon className="w-4 h-4 shrink-0" /> {item.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-cc-border">
          <button onClick={doLogout} data-testid="admin-logout" className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 font-body text-sm font-medium text-cc-sub hover:bg-cc-hot/10 hover:text-cc-hot transition-colors">
            <LogOut className="w-4 h-4" /> Uitloggen
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="lg:pl-[264px]">
        <header className="sticky top-0 z-20 h-16 bg-cc-bg/80 backdrop-blur border-b border-cc-border flex items-center gap-3 px-5 lg:px-8">
          <button onClick={() => setOpen(true)} className="lg:hidden text-cc-sub"><Menu className="w-6 h-6" /></button>
          <div className="min-w-0">
            <h1 className="font-heading font-extrabold text-lg text-cc-ink leading-tight truncate" data-testid="page-title">{title}</h1>
            {subtitle ? (
              <p className="font-body text-xs text-cc-faint truncate">{subtitle}</p>
            ) : (
              <p className="font-body text-xs text-cc-faint capitalize">{now.toLocaleDateString("nl-BE", { weekday: "long", day: "2-digit", month: "long" })} · {now.toLocaleTimeString("nl-BE")}</p>
            )}
          </div>
          <div className="ml-auto flex items-center gap-3">
            {actions}
            <span className="hidden sm:inline-flex items-center gap-2 rounded-full border border-cc-border px-3 py-1.5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cc-pos opacity-70"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cc-pos"></span>
              </span>
              <span className="font-body text-xs font-semibold text-cc-sub">Live</span>
            </span>
            <span className="hidden md:inline font-body text-xs text-cc-faint truncate max-w-[180px]" data-testid="admin-user">{user?.email}</span>
          </div>
        </header>
        <main className="px-5 lg:px-8 py-7 max-w-[1440px]">{children}</main>
      </div>
    </div>
  );
}
